import React, { useState, useEffect } from 'react';
import { API_BASE_URL } from '../config';
import { useNavigate } from 'react-router-dom';
import './MomentumRadar.css';
import RefreshButton from './RefreshButton';
import WatchlistStar from './WatchlistStar';

const ShortTermLOM = ({ onBack }) => {
  const [stocks, setStocks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [timeframe, setTimeframe] = useState('5min');
  const [trend, setTrend] = useState('high');

  const fetchData = async () => {
      setLoading(true);
      setError(null);
      
      try {
        const response = await fetch(`${API_BASE_URL}/short-term-lom`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            timeframe: timeframe === '5min' ? '5min' : '15min',
            trend: trend,
            category: 'fo'
          })
        });

        if (!response.ok) {
          throw new Error('Failed to fetch short term LOM data');
        }

        const result = await response.json();
        if (result.status === 'success' && Array.isArray(result.data)) {
          setStocks(result.data);
        } else {
          setStocks([]);
        }
      } catch (err) {
        console.error('Error fetching short term LOM data:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

  useEffect(() => {
    fetchData();
  }, [timeframe, trend]);

  const handleTimeframeChange = (newTimeframe) => {
    setTimeframe(newTimeframe);
  };

  const handleTrendChange = (newTrend) => {
    setTrend(newTrend);
  };

  return (
    <div className="short-term-lom">
      <header className="lom-header">
        <div className="lom-header-container">
          <div className="lom-header-left">
            <button className="back-button" onClick={onBack}>
              <span className="back-arrow">←</span> Back
            </button>
            <h1>{timeframe === '5min' ? 'LOSS OF MOMENTUM' : 'LOSS OF MOMENTUM'}</h1>
            <RefreshButton 
              onRefresh={fetchData} 
              isLoading={loading}
              size="small"
              style={{ marginLeft: '15px' }}
            />
          </div>
          <div className="lom-header-right">
            <div className="timeframe-selector">
              <button
                className={`timeframe-btn ${timeframe === '5min' ? 'active' : ''}`}
                onClick={() => setTimeframe('5min')}
              >
                5 Min
              </button>
              <button
                className={`timeframe-btn ${timeframe === '15min' ? 'active' : ''}`}
                onClick={() => setTimeframe('15min')}
              >
                15 Min
              </button>
            </div>
            <div className="trend-selector">
              <button
                className={`trend-btn high ${trend === 'high' ? 'active' : ''}`}
                onClick={() => handleTrendChange('high')}
              >
                HIGH
              </button>
              <button
                className={`trend-btn low ${trend === 'low' ? 'active' : ''}`}
                onClick={() => handleTrendChange('low')}
              >
                LOW
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="lom-table-container">
        <table className="lom-table">
          <thead>
            <tr>
              <th>Symbol</th>
              <th>LOM Time</th>
              <th>% Change</th>
              <th>LTP</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="4" className="loading-cell">
                  <div className="loader"></div>
                </td>
              </tr>
            ) : error ? (
              <tr>
                <td colSpan="4" className="error-cell">
                  {error}
                </td>
              </tr>
            ) : stocks.length > 0 ? (
              stocks.map((stock, index) => {
                // Format time to 12-hour format with AM/PM
                const lomTime = new Date(stock.lom_time);
                const formattedTime = lomTime.toLocaleTimeString('en-US', { 
                  hour: 'numeric', 
                  minute: '2-digit',
                  hour12: true
                });
                
                // Format percentage with sign and color
                const percentChange = stock.percent_change || 0;
                const isPositive = percentChange >= 0;
                const formattedPercent = (isPositive ? '+' : '') + percentChange.toFixed(2) + '%';
                
                // Format LTP with 2 decimal places
                const formattedLTP = typeof stock.ltp === 'number' 
                  ? stock.ltp.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                  : stock.ltp || 'N/A';
                
                return (
                  <tr key={index} className="lom-table-row">
                    <td className="symbol-cell">
                      <WatchlistStar 
                        stock={{
                          symbol: stock.symbol,
                          name: stock.symbol,
                          ltp: stock.ltp,
                          change: stock.change,
                          time: stock.time
                        }} 
                        size="16px"
                      />
                      {stock.symbol}
                    </td>
                    <td>{formattedTime}</td>
                    <td className={isPositive ? 'positive' : 'negative'}>
                      {formattedPercent}
                    </td>
                    <td>{formattedLTP}</td>
                  </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan="4" className="no-data">No data available</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ShortTermLOM;
